import type { IFHPConfig } from '@nab/x-types';
import { DataSecurityMgr } from '../DataSecurityMgr';
import { sdkLoggerInterface as log } from '../../Logger/Logger';

/**
 * @class FHPManager
 * The FHPManager is used to enable Group-IB Fraud Hunting Platform which is Client-side digital identity protection and fraud prevention in real time.
 * The class is responsible for creating an instance of FHP library and setting unique session ID.
 *
 * @example
 * To set this up in a shell you'll need to know the Library URL,
 * When Shell loaded Miniapps via initRuntime() function, SDK will create a instance of the class and append a script tag up into the DOM.
 *
 * **NOTE** Shell only needs to config the fhp in serviceConfig as the example below, as this initialisation happens within AppLoader by default
 *
 * ```typescript
 * const configFHP = {
 *     cid: 'gib-w-nab-ib-dev',
 *     backUrl: '//sbbe.group-ib.com/api/fl'
 * }
 * const fhp = new FHPManager("https://URL_TO_THE_LIBRARY", configFHP);
 * console.log(fhp) // unique session id returned
 * const fhpCSID: string = await fhp.getData();
 * ```
 *
 * @example
 * Shell can configure the FHP library via the service configuration.
 * The configuration allow to set an library URL and an _optional_ config value that will allow then to re-use any existing **CSID**.
 * By default the CSID will be auto generated as unique GUID, if the config is not provided.
 * To enable this Library in your application.
 *
 * ```typescript
 * initiRuntime({
 *   ...
 *   userConfig: {
 *     type: 'customer',
 *     org: 'nab',
 *     id: 'logged-in-user-id' // Optional
 *     authenticated: true // Optional
 *     sessionId: 'existing-sessionId' // Optional
 *   },
 *   servicesConfig: {
 *     dataSecurity: {
 *       fhp: {
 *         // LibraryUrl below should be provided by Fraud team.
 *         // Otherwise you can configure the proxy in your shell app (nginx.conf) to
 *         // proxy_pass the location '/fhp/js-lib' (from LibraryUrl below) to
 *         // FHP vendor's js file "https://vojs.group-ib.ru/build/main__48582226_644_1975.js"
 *         LibraryUrl: `${/^.*\//.exec(window.location.pathname)[0]}fhp/js-lib`,
 *
 *         configValue: {
 *           // backUrl below should be provided by Fraud team.
 *           // Otherwise you can configure the proxy in your shell app (nginx.conf) to
 *           // proxy_pass the location '/fhp/api' (from backUrl below) to
 *           // FHP vendor's api url "https://sb-au-be.group-ib.com/api/fl"
 *           backUrl: `${/^.*\//.exec(window.location.pathname)[0]}fhp/api`,
 *
 *            // cid could be one of the following strings:
 *            // - NAB IB: "gib-w-nab-ib-dev", "gib-w-nab-ib-sit", "gib-w-nab-ib"
 *            // - NAB Connect: "gib-w-nab-con-dev", "gib-w-nab-con-sit", "gib-w-nab-con"
 *            cid: 'gib-w-nab-con-dev',
 *
 *            // Optional configuration to send in any existing CSID
 *            // This csid property is still here just for backward-compatibility.
 *            // You can remove this one and set the sessionId in userConfig above instead. <start>
 *            csid: 'existing-sessionId'
 *            // <end>
 *          }
 *       }
 *     }
 *   }
 *   ...
 * })
 * ```
 */
class FHPManager extends DataSecurityMgr<IFHPConfig, string> {
    private GLOBAL_FHP_VAR_NAME = 'gib';

    /**
     * Used to set the configuration and the library URI to load the unique ID for FHP data.
     * @param cdnUrl Library URI to be loaded. Usually a CDN path.
     * @param config FHP config.
     * ```typescript
     * {
     *     cid: "[gib-w-<id>]", // Mandatory
     *     backUrl: "[URL to customer proxy or //sbbe.group-ib.com/api/fl]", // Mandatory
     *     csId: "[existing-sessionId. For backward-compatibility only. Set userConfig.sessionId instead]" // Optional
     *     rsaModulus: "[Modulus of RSA public key in hex string]", // Optional
     *     gafUrl: "[URL to iframe]", // Optional
     *     forceFirstAlive: true, // Optional
     *     silentAlive: true, // Optional
     *     cookiesDomain: "[Subdomains, for which the attributes will be saved in cookies]" // Optional
     *     userId: "<id>" // Optional
     * }
     * ```
     * Read more [here](https://fhwiki.group-ib.tech/Integration/js_faq/#Adding%20Web%20Snippet%20to%20the%20pages%20of%20protected%20resource)
     */
    constructor(cdnUrl: string, config?: IFHPConfig) {
        super(cdnUrl, config);
        this.initialise();
    }

    /**
     * Required to be initialized on load of the application.
     * This will load the FHP library using the specified cdnURL.
     */
    public async initialise(): Promise<boolean> {
        let isScriptLoaded = !!window[this.GLOBAL_FHP_VAR_NAME];
        if (!isScriptLoaded) {
            isScriptLoaded = await this.loadLibrary();
        }
        if (isScriptLoaded) {
            return this.initFHP();
        }
        return false;
    }

    /**
     * Initializing FHP Web Snippet
     */
    protected initFHP(): boolean {
        let returnValue = false;
        if (window[this.GLOBAL_FHP_VAR_NAME]?.init && this.config && this.config.cid && this.config.backUrl) {
            const { cid, backUrl, rsaModulus, gafUrl, forceFirstAlive, silentAlive, cookiesDomain } = this.config;
            window[this.GLOBAL_FHP_VAR_NAME].init({
                cid,
                backUrl,
                rsaModulus,
                gafUrl,
                forceFirstAlive,
                silentAlive,
                cookiesDomain
            });
            this.setDetails(this.config);
            returnValue = true;
        }
        return returnValue;
    }

    private setDetails(data: Partial<IFHPConfig>): void {
        const windowGIB = window[this.GLOBAL_FHP_VAR_NAME];
        this.config = { ...this.config, ...data };
        if (windowGIB) {
            const authStatus = this.config.authenticated ? windowGIB.IS_AUTHORIZED : windowGIB.IS_GUEST;
            windowGIB.setAuthStatus(authStatus);
            windowGIB.setSessionID(this.config.csId);
            windowGIB.setLogin(this.config.userId || '');
        }
    }

    public updateData(data?: Partial<IFHPConfig>): boolean {
        this.setDetails(data);
        if (window[this.GLOBAL_FHP_VAR_NAME]) {
            return true;
        }
        log.warn('Data security feature for FHP not initialised.');
        return false;
    }

    public async getData(): Promise<string> {
        if (window[this.GLOBAL_FHP_VAR_NAME] && this.config.csId) {
            return this.config.csId;
        }
        return Promise.reject(new Error('Data security feature for FHP not initialised.'));
    }
}

export { FHPManager };
